import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fetchFusionAccess } from "@/lib/useFusionAccess";

/** /fusion/login — dedicated sign-in for Fusion athletes & coaches, separate from the BioHacker login. */
export default function FusionLogin() {
  const nav = useNavigate();
  const { setUser, fetchTeam } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      setUser(data);
      if (data?.team_id) await fetchTeam();
      const access = await fetchFusionAccess();
      if (access.fusion_enabled || !access.biohacker_enabled) {
        nav(data.role === "coach" || data.role === "admin" ? "/dashboard/fusion" : "/portal/fusion", { replace: true });
      } else {
        toast.info("No Fusion campaign on your team yet — taking you to your BioHacker home.");
        nav(data.role === "admin" ? "/admin" : data.role === "coach" ? "/dashboard" : "/portal", { replace: true });
      }
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const cls = "bg-white/5 border-white/15 text-white placeholder:text-white/35";
  return (
    <div className="min-h-screen bg-[#080e1c]" data-testid="fusion-login-page">
      <div className="max-w-md mx-auto px-5 pb-20">
        <header className="pt-16 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center bg-amber-400/15 border border-amber-400/30">
            <Flame className="h-7 w-7 text-amber-300" />
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300">BioHacker Fusion™</div>
          <h1 className="text-3xl font-black text-white mt-2">Fusion sign in</h1>
          <p className="text-sm text-white/55 mt-2">For Hit-A-Thon athletes and coaches. Raise. Measure. Develop. Repeat.™</p>
        </header>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3" data-testid="fusion-login-form">
          <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={cls} data-testid="fusion-login-email" />
          <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={cls} data-testid="fusion-login-password" />
          <Button type="submit" disabled={busy || !email || !password} className="w-full font-black text-[#0b1424] bg-amber-400 hover:bg-amber-300 py-6" data-testid="fusion-login-submit">
            {busy ? "Signing in…" : "Sign in to Fusion"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="text-[11px] text-white/40 text-center space-y-1">
            <div>Athletes: use the account you created from your coach's invite link.</div>
            <div>
              School not signed up yet? <Link to="/fusion" className="underline text-amber-300/90">Start a Fusion event</Link>
              <span className="mx-1.5">·</span>
              BioHacker platform login? <Link to="/login" className="underline text-white/60" data-testid="link-biohacker-login">Sign in here</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
